import { lazy, Reducer, Suspense, useEffect, useMemo, useReducer, useState } from "react";
// React Query
import { useQueryClient } from "@tanstack/react-query";

// Context
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";

// MUI
import { Skeleton, Stack, Typography } from "@mui/material";

// Helpers
import { getIdentifiers } from "@/DataPages/helpers/salesOperations/salesPerformance/utils";
import { getSalesOperationName } from "@DataPages/components/SalesOperations/constants";

// Context
import { CrossStoreReconciliationContextProvider } from "@DataPages/context/salesOperations/salesPerformance/CrossStoreReconciliationContext";

// Components
import EmptyBody from "@Layouts/components/Basics/EmptyBody";
import SalesPerformanceTable from "./Tables/SalesPerformanceTable";
import TableLoader from "@Layouts/components/Skeletons/TableLoader";
import PayCrossStoreCommissionsButton from "@DataPages/components/SalesOperations/Tabs/SalesPerformance/components/CrossStoreCommissionsDialog/Widgets/PayCrossStoreCommissionsButton";

// Hooks
import { useLocationId } from "@Common/hooks/Location/useLocationId";

// Images
import empty from "@/assets/images/commons/empty.png";
import emptyTable from "@/assets/images/commons/empty_table.png";

// Types
import type { IUpdateSalesPerformanceSalesResponse } from "@DataPages/types/salesOperations/salesPerformance/model";
import type { ISidebarItem } from "@Common/interfaces/sidebar";
import type { Nullable } from "@Common/interfaces/types";
import type { ISalesPerformance } from "@DataPages/types/salesOperations/salesPerformance/models/salesPerformance";

// Lazy load the dialog component
const CrossStoreCommissionsDialog = lazy(
	() =>
		import(
			"@DataPages/components/SalesOperations/Tabs/SalesPerformance/components/CrossStoreCommissionsDialog/CrossStoreCommissionsDialog"
		)
);

interface ISalesPerformanceModelProps {
	data: ISalesPerformance[] | undefined;
	dataPresent: boolean;
	isLoading: boolean;
	selected: Nullable<ISidebarItem>;
}
type OemMultiSelect = {
	_id: string;
	oemName: string;
	dealerCodeFS: string;
	label: string;
};

type TSalesPerformanceSwitchVariant = "Commissionable" | "Operational";
type TFilterAction = {
	type: "switchVariant" | "selectOem";
	payload: SalesPerformanceFilterState;
};

type SalesPerformanceFilterState = Partial<{
	salesPerformanceSwitchVariant: TSalesPerformanceSwitchVariant;
	selectedOems: OemMultiSelect[];
}>;

type TGetSalesToShow = {
	sales: ISalesPerformance[] | undefined;
	locationId: string;
	selectedOems: OemMultiSelect[];
	salesPerformanceSwitchVariant: TSalesPerformanceSwitchVariant;
};

/***************************************************** Helper Functions *****************************************************/
function getCommissionableSalesPerformances({ sales, locationId }: { sales: ISalesPerformance[]; locationId: string }) {
	if (!sales?.length) {
		return [];
	}
	return sales.filter((sale) => isSalesCommissionable(sale, locationId));
}

function isSalesCommissionable(sale: ISalesPerformance, locationId: string): boolean {
	const { salesEmployeeLocationId, financeEmployeeLocationId, salesManagerLocationId } = sale;
	return [salesEmployeeLocationId, financeEmployeeLocationId, salesManagerLocationId].some((location) => location === locationId);
}

function getOperationalSalesPerformances({
	sales,
	selectedOems,
	locationId,
}: {
	locationId: string;
	sales: ISalesPerformance[];
	selectedOems?: OemMultiSelect[];
}) {
	if (!selectedOems?.length) {
		return [];
	}

	const isPreOwnedIncludes = selectedOems?.some((oem) => oem.oemName === "Pre-Owned");
	const availableSales = isPreOwnedIncludes ? sales : sales.filter((sale) => sale.saleType !== "Used");

	const salesToReturn = availableSales?.filter?.((sale) => sale?.sourceLocationId === locationId);
	const dealerCodes = selectedOems?.map?.((item) => item?.dealerCodeFS).filter((dealerCodeFS) => dealerCodeFS?.length);
	if (!dealerCodes?.length) {
		return salesToReturn;
	}

	return salesToReturn.filter?.((sale) => isSaleOperational(sale, dealerCodes));
}

function isSaleOperational(sale: ISalesPerformance, dealerCodes: string[]) {
	return sale?.saleType === "Used" || (sale.saleType === "New" && sale?.dealerCodeFS && dealerCodes.includes(sale?.dealerCodeFS));
}

function getFilteredSales(args: TGetSalesToShow): ISalesPerformance[] {
	const { salesPerformanceSwitchVariant, selectedOems, sales, locationId } = args;

	if (!sales?.length) {
		return [];
	}

	switch (salesPerformanceSwitchVariant) {
		case "Commissionable":
			return getCommissionableSalesPerformances({ sales, locationId });
		case "Operational":
			return getOperationalSalesPerformances({ sales, locationId, selectedOems });
		default:
			return [];
	}
}

const salesPerformanceInitialState: SalesPerformanceFilterState = {
	salesPerformanceSwitchVariant: "Commissionable",
	selectedOems: [],
};
function updateSalesPerformanceFilterReducer(state: SalesPerformanceFilterState, action: TFilterAction): SalesPerformanceFilterState {
	switch (action.type) {
		case "switchVariant":
			return { ...state, salesPerformanceSwitchVariant: action.payload.salesPerformanceSwitchVariant, selectedOems: [] };
		case "selectOem":
			return { ...state, selectedOems: action.payload.selectedOems };
		default:
			return state;
	}
}

const SALES_PERFORMANCE_QUERY_KEY = getSalesOperationName("QUERY", "SALES_PERFORMANCE");
const SALES_PERFORMANCE_SUBSCRIPTION_KEY = getSalesOperationName("SUBSCRIPTION", "SALES_PERFORMANCE");
export default function SalesPerformanceSection(props: Readonly<ISalesPerformanceModelProps>) {
	const { dataPresent, data: salesPerformance, selected, isLoading } = props;

	// Hooks
	const queryClient = useQueryClient();
	const { getSubscription } = useSubscriptions();
	const locationId = useLocationId();

	// State
	const [currentSalesPerformanceFilterState, dispatch] = useReducer<Reducer<SalesPerformanceFilterState, TFilterAction>>(
		updateSalesPerformanceFilterReducer,
		salesPerformanceInitialState
	);
	const [isDialogOpen, setIsDialogOpen] = useState(false);

	/********************************************* SUBSCRIPTIONS ************************************************/
	const salesPerformanceSubscription = getSubscription([SALES_PERFORMANCE_SUBSCRIPTION_KEY, selected?._id]);
	salesPerformanceSubscription?.subscriptions?.onUpdateSalesPerformanceSales?.setCallback(
		(result: IUpdateSalesPerformanceSalesResponse) => {
			const updatedSalesPerformanceRecords = result.data?.onUpdateSalesPerformanceSales;
			if (!updatedSalesPerformanceRecords) {
				return null;
			}
			queryClient.invalidateQueries({ queryKey: [SALES_PERFORMANCE_QUERY_KEY, locationId, selected?._id] }).then(() => {});
		}
	);

	// Filter the performance data based upon the Switch and/or the Selected OEMs
	const rows = useMemo(() => {
		const { salesPerformanceSwitchVariant = "Commissionable", selectedOems = [] } = currentSalesPerformanceFilterState;
		return getFilteredSales({
			locationId,
			selectedOems,
			salesPerformanceSwitchVariant,
			sales: salesPerformance,
		});
	}, [locationId, salesPerformance, currentSalesPerformanceFilterState]);

	/********************************************* HANDLERS ************************************************/
	function handleSwitchVariant(variant: TSalesPerformanceSwitchVariant, oemIdentifiers: OemMultiSelect[]) {
		if (variant === "Operational") {
			dispatch({ type: "selectOem", payload: { selectedOems: oemIdentifiers } });
		} else {
			dispatch({ type: "selectOem", payload: { selectedOems: [] } });
		}
	}

	function handleDialogOpen() {
		setIsDialogOpen(true);
	}

	function handleDialogClose() {
		setIsDialogOpen(false);
	}

	useEffect(() => {
		const oemIdentifiers = getIdentifiers(salesPerformance, locationId);

		handleSwitchVariant(
			currentSalesPerformanceFilterState.salesPerformanceSwitchVariant as TSalesPerformanceSwitchVariant,
			oemIdentifiers as unknown as OemMultiSelect[]
		);
	}, [locationId, salesPerformance, currentSalesPerformanceFilterState.salesPerformanceSwitchVariant]);

	if (isLoading) {
		return (
			<Stack spacing={4} pt={3} pb={8}>
				<Stack direction={"row"} justifyContent={"space-between"} alignContent={"center"}>
					<Skeleton variant="rectangular" height={25} width={350} sx={{ borderRadius: "6px" }} />
					<Skeleton variant="rectangular" height={25} width={150} sx={{ borderRadius: "6px" }} />
				</Stack>
				<TableLoader content multiTitles={5} rows={7} />
			</Stack>
		);
	}

	if (!salesPerformance?.length) {
		return (
			<EmptyBody
				image={dataPresent ? emptyTable : empty}
				title={dataPresent ? "No Sales Performance to show" : "No Sales Performance selected"}
				bodyHeight={dataPresent ? "435px" : "510px"}
				sx={{
					boxShadow: "none !important",
				}}
			/>
		);
	}

	return (
		<Stack pt={3} pb={6} component={"section"}>
			<Stack direction={"row"} justifyContent={"space-between"} alignContent={"center"}>
				<Typography variant={"h2"} component={"h2"}>
					{`${props.selected?.title || ""} Sales Performance`.trim()}
				</Typography>
				<PayCrossStoreCommissionsButton
					isDataLoading={isLoading}
					isExportable={selected?.status === "CLOSING"}
					// Callbacks
					onClick={handleDialogOpen}
				/>
			</Stack>
			<SalesPerformanceTable
				// Data
				locationId={locationId}
				rows={rows}
				operational={currentSalesPerformanceFilterState.salesPerformanceSwitchVariant === "Operational"}
				selectedOems={currentSalesPerformanceFilterState.selectedOems ?? []}
				identifiers={getIdentifiers(salesPerformance, locationId)}
				// Table Options
				useSwitch
				tableKey="sales-performance"
				// Callback
				onSelectOEM={(values) => {
					dispatch({ type: "selectOem", payload: { selectedOems: values as unknown as OemMultiSelect[] } });
				}}
				onUseSwitch={(value) =>
					dispatch({
						type: "switchVariant",
						payload: { salesPerformanceSwitchVariant: value ? "Operational" : "Commissionable" },
					})
				}
			/>
			{isDialogOpen && (
				<Suspense>
					<CrossStoreReconciliationContextProvider locationId={locationId}>
						<CrossStoreCommissionsDialog
							// Data
							selectedCommissionCycle={selected}
							// State
							open={isDialogOpen}
							// Callbacks
							onCloseDialog={handleDialogClose}
						/>
					</CrossStoreReconciliationContextProvider>
				</Suspense>
			)}
		</Stack>
	);
}
